import { Alert, Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useEffect, useRef, useState } from 'react';
import { CopilotDisclaimer } from './CopilotDisclaimer';
import { CopilotLoadingState } from './CopilotLoadingState';
import { CopilotMessageList } from './CopilotMessageList';
import { CopilotInput } from './CopilotInput';
import { CopilotSuggestedActions } from './CopilotSuggestedActions';

function errorMessage(error) {
  return error?.response?.data?.message || 'G.AI could not generate a response. Please try again.';
}

export const CopilotPanel = (props) => {
  const {
    open,
    title = 'G.AI',
    request,
    onClose,
    followUpRequest,
    suggestedActions = [],
    autoRun = true,
    emptyStateText = 'Ask G.AI about your carbon data, or choose a suggested action.',
    // Optional and additive: when a page passes onUseDraft(text), each finished answer gets a
    // button (labelled useDraftLabel) that hands the text to the page, which puts it into a field
    // for the user to review and save. Without it the panel behaves exactly as before.
    onUseDraft,
    useDraftLabel = 'Use as commentary',
  } = props;
  // Running transcript of the conversation: { id, role: 'user' | 'assistant', content, warnings }.
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const panelWasOpenRef = useRef(false);
  const messageIdRef = useRef(0);
  const bottomRef = useRef(null);

  const nextMessageId = () => {
    messageIdRef.current += 1;
    return messageIdRef.current;
  };

  const runRequest = async (requestFn, promptLabel) => {
    if (!requestFn) return;
    setLoading(true);
    setError('');
    if (promptLabel) {
      setMessages((prev) => [...prev, { id: nextMessageId(), role: 'user', content: promptLabel }]);
    }
    try {
      const response = await requestFn();
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), role: 'assistant', content: response.answer, warnings: response.warnings || [] },
      ]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      const justOpened = !panelWasOpenRef.current;
      panelWasOpenRef.current = true;
      if (justOpened) {
        // Clear the transcript on each open so answers from one record/report don't bleed into another.
        setMessages([]);
        setError('');
        if (autoRun && request) {
          runRequest(request);
        }
      }
    } else {
      panelWasOpenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request, autoRun]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  const copyHandler = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(console.log);
  };

  const followUpHandler = (question) => {
    if (!followUpRequest) return;
    // Send the transcript so far so the model can answer in context. The new
    // question is appended by runRequest after this snapshot is taken.
    const history = messages.map((message) => ({ role: message.role, content: message.content }));
    runRequest(() => followUpRequest(question, history), question);
  };

  const resolvedSuggestedActions = suggestedActions.map((action) => ({
    ...action,
    onClick: action.onClick ||
      (action.request ? () => runRequest(action.request, action.label) : undefined) ||
      (action.question ? () => followUpHandler(action.question) : undefined),
  }));

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiDrawer-paper': {
          pointerEvents: 'auto',
        },
      }}
      PaperProps={{
        sx: (theme) => ({
          // Align with fixed AppBar height in top-navbar so the header stays full-width and the pane sits beneath it.
          top: { xs: 0, sm: 64 },
          height: { xs: '100%', sm: 'calc(100vh - 64px)' },
          borderTopLeftRadius: { xs: 0, sm: 24 },
          borderBottomLeftRadius: { xs: 0, sm: 24 },
          overflow: 'hidden',
          boxShadow: '-12px 0 32px rgba(17, 30, 33, 0.14)',
          borderLeft: '1px solid',
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer,
        }),
      }}
    >
      <Box
        sx={{
          width: { xs: '100vw', sm: 520 },
          maxWidth: '100vw',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fbfcfe',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, #7BA7FF 0%, #8AD38B 100%)',
                color: 'common.white',
              }}
            >
              <AutoAwesomeOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1.1 }}>
                G.AI
              </Typography>
              {title !== 'G.AI' && (
                <Typography variant="caption" color="text.secondary">
                  {title}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton aria-label="Close G.AI" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 2 }}>
          <CopilotSuggestedActions actions={resolvedSuggestedActions} disabled={loading} />
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}
          {messages.length === 0 && !loading && !error && (
            <Box
              sx={{
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                px: 4,
              }}
            >
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                Ask G.AI
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {emptyStateText}
              </Typography>
            </Box>
          )}
          <CopilotMessageList messages={messages} onCopy={copyHandler} onUseDraft={onUseDraft} useDraftLabel={useDraftLabel} />
          {loading && <CopilotLoadingState />}
          <Box ref={bottomRef} />
        </Box>
        <Box sx={{ p: 3, pt: 1.5, bgcolor: '#fbfcfe' }}>
          {followUpRequest && (
            <CopilotInput disabled={loading} onSubmit={followUpHandler} />
          )}
          <CopilotDisclaimer />
        </Box>
      </Box>
    </Drawer>
  );
};
